import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import {
  applyTypography,
  BaseButton,
  trackWorkshopBookComplete,
  useCopyToast,
  useT,
} from '@repo/common';
import { ReactComponent as NavigationLeftIcon20 } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { ReactComponent as CheckCircleIcon } from '@repo/common/assets/icons/32px/icon_32px_check-circle_filled.svg';
import { ReactComponent as CopyIcon } from '@repo/common/assets/icons/24px/icon_24px_copy.svg';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/24px/icon_24px_download.svg';
import WorkshopCard from '../../components/Kpathport/PathFeed/WorkshopCard';
import {
  formatDate,
  formatTime,
} from '../../components/Kpathport/utils/dateTimeUtils';
import { getPaymentByOrderId } from '../../api/payment/payment.api';
import { getBooking } from '../../api/booking/booking.api';
import { getWorkshopDetail } from '../../api/workshop/workshop.api';
import RefundModal from '../../components/Kpathport/Modal/RefundModal';

const DEFAULT_RECOMMENDED_WORKSHOPS = [];

// 좌석을 실제로 차지한 "확정" 상태들. ATTENDED/ABSENT 는 확정 후 출석 처리된 예약이다
const CONFIRMED_STATUSES = ['CONFIRMED', 'ATTENDED', 'ABSENT'];

const BookingConfirmed = () => {
  const t = useT();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { copy, copyToast } = useCopyToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [bookingItems, setBookingItems] = useState([]);
  const [infoModal, setInfoModal] = useState(null); // 'thingsToKnow' | 'refundPolicy'

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const orderId = searchParams.get('orderId');
        const bookingIdsParam =
          searchParams.get('bookingIds') || searchParams.get('bookingId');

        let bookingIds = [];

        if (orderId) {
          const payment = await getPaymentByOrderId(orderId);
          setPaymentData(payment);
          bookingIds =
            payment.bookingIds ||
            (payment.bookingId ? [payment.bookingId] : []);
        } else if (bookingIdsParam) {
          bookingIds = bookingIdsParam
            .split(',')
            .map((id) => parseInt(String(id).trim(), 10))
            .filter((id) => !isNaN(id));
          setPaymentData(null);
        } else {
          setError(
            t('Booking information is missing.', '예약 정보가 없습니다.'),
          );
          setLoading(false);
          return;
        }

        if (bookingIds.length > 0) {
          const bookingPromises = bookingIds.map(async (bookingId) => {
            const booking = await getBooking(bookingId);

            let workshop = null;
            let dateSlot = null;

            if (booking.workshopId) {
              workshop = await getWorkshopDetail(booking.workshopId);

              if (workshop.category === 'CULTURE') {
                localStorage.setItem('step01_booking_status', 'ongoing');
              }

              if (booking.workshopDateSlotId && workshop.dateSlots) {
                dateSlot = workshop.dateSlots.find(
                  (slot) => slot.id === booking.workshopDateSlotId,
                );
              }
            }

            return {
              bookingId,
              booking,
              workshop,
              dateSlot,
            };
          });

          const bookingResults = await Promise.all(bookingPromises);

          if (!hasTrackedRef.current) {
            bookingResults.forEach((item) => {
              const workshop = item.workshop;
              const dateSlot = item.dateSlot;

              if (!workshop || !dateSlot) return;
              // 대기 명단·결제 대기 예약은 "예약 완료" 이벤트가 아니다
              if (
                !CONFIRMED_STATUSES.includes(
                  String(item.booking?.status || '').toUpperCase(),
                )
              ) {
                return;
              }

              trackWorkshopBookComplete({
                workshop_id: String(workshop.id),
                workshop_name: workshop.title,
                workshop_date: dateSlot.date,
                booking_status: 'upcoming',
                step_number: workshop.category,
              });
            });

            hasTrackedRef.current = true;
          }

          setBookingItems(bookingResults);
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch booking data:', err);
        setError('예약 정보를 불러오는데 실패했습니다.');
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const userName =
    paymentData?.customerName ||
    (bookingItems.length > 0 ? bookingItems[0].booking?.customerName : '') ||
    '';
  const recommendedWorkshops = DEFAULT_RECOMMENDED_WORKSHOPS;

  // 이 페이지는 무료 예약(bookingIds)·카드 결제(orderId) 둘 다 쓴다. 정원이 찬 무료 워크숍은 WAITLISTED 로
  // 만들어지고, 결제가 끝나지 않은 주문 ID 로도 들어올 수 있으므로 상태를 보고 머리말을 고른다.
  // 전부 "예약 완료" 로 보이면 대기자가 확정된 줄 알게 된다.
  const bookingStatuses = bookingItems.map(({ booking }) =>
    String(booking?.status || '').toUpperCase(),
  );
  const paymentStatus = String(paymentData?.paymentStatus || '').toUpperCase();
  const isPaymentPending =
    Boolean(paymentData) &&
    !['DONE', 'PARTIAL_CANCELED'].includes(paymentStatus);
  const allConfirmed =
    bookingStatuses.length > 0 &&
    bookingStatuses.every((status) => CONFIRMED_STATUSES.includes(status));
  const hasWaitlisted = bookingStatuses.includes('WAITLISTED');
  const pageState = isPaymentPending
    ? 'pending'
    : allConfirmed
      ? 'confirmed'
      : hasWaitlisted
        ? 'waitlisted'
        : 'partial';
  const headerTitle = {
    confirmed: t('Booking Confirmed', '예약 완료'),
    waitlisted: t("You're on the waitlist", '대기 명단에 올랐어요'),
    pending: t('Payment pending', '결제 대기 중'),
    partial: t('Booking details', '예약 상세'),
  }[pageState];
  const headline = {
    confirmed: t('See you on the day of the workshop', '워크숍 당일에 만나요'),
    waitlisted: t(
      "We'll email you as soon as a spot opens up",
      '자리가 나면 바로 이메일로 알려드릴게요',
    ),
    pending: t(
      'Your booking will be confirmed once the payment is completed',
      '결제가 완료되면 예약이 확정됩니다',
    ),
    partial: t(
      'Some bookings are not confirmed yet. Check My page for the latest status',
      '아직 확정되지 않은 예약이 있어요. 마이페이지에서 상태를 확인해 주세요',
    ),
  }[pageState];
  const statusLabel = (status) =>
    ({
      WAITLISTED: t('Waitlist', '대기'),
      HOLD: t('Payment pending', '결제 대기'),
      OFFERED: t('Spot offered', '자리 제안'),
      CANCELED: t('Canceled', '취소됨'),
      EXPIRED: t('Expired', '만료됨'),
      REFUNDED: t('Refunded', '환불됨'),
    })[String(status || '').toUpperCase()] || status;

  const onDownloadReceipt = () => {
    window.open(paymentData.receiptUrl, '_blank');
  };

  const onThingsToKnow = () => setInfoModal('thingsToKnow');
  const onCancellationPolicy = () => setInfoModal('refundPolicy');

  // 예약한 워크숍의 안내문 (여러 건이면 워크숍명으로 구분해 합친다)
  const isMultiBooking = bookingItems.length > 1;
  const thingsToKnowText = bookingItems
    .map(({ workshop }) => {
      const text = (workshop?.thingsToKnow || '').trim();
      if (!text) return '';
      return isMultiBooking ? `[${workshop?.title || ''}]\n${text}` : text;
    })
    .filter(Boolean)
    .join('\n\n');

  const refundPolicyText = bookingItems
    .map(({ workshop }) => (workshop?.refundPolicy || '').trim())
    .find(Boolean);

  const handleCopy = (id) => copy(id);

  if (loading) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-grayscale-gray01'>
        <p className={`${applyTypography('h5')} text-text-primary`}>
          예약 정보를 불러오는 중..
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-grayscale-gray01'>
        <p className={`${applyTypography('h5')} text-text-error mb-4`}>
          오류 발생
        </p>
        <p className={`${applyTypography('b16')} text-text-primary mb-8`}>
          {error}
        </p>
        <BaseButton
          type='primary'
          size='m'
          hideIcon
          onClick={() => navigate('/community/kpathport/pathfeed')}
          className='bg-green-deep02 hover:bg-green-deep02 h-auto min-w-0 border-0 text-text-inverse px-6 py-3 rounded-[8px] hover:opacity-90 transition-opacity'
        >
          피드로 이동
        </BaseButton>
      </div>
    );
  }

  return (
    <>
    <div className='flex flex-col items-center relative w-full min-h-screen bg-grayscale-gray01 px-[16px] pt-0 pb-[80px] tabletH:px-[80px] tabletH:pt-[120px] tabletH:pb-[400px]'>
      <div className='tabletH:hidden bg-grayscale-gray01 content-stretch flex flex-col items-start overflow-clip pb-[16px] pt-[16px] px-[16px] relative shrink-0 w-[calc(100%+32px)] -mx-[16px]'>
        <div className='relative flex items-center justify-between w-full content-stretch shrink-0'>
          <div className='flex flex-row items-center self-stretch'>
            <div className='relative flex items-center h-full content-stretch shrink-0'>
              <BaseButton
                type='text'
                size='s'
                onClick={() => navigate(-1)}
                className='bg-grayscale-gray01 hover:bg-grayscale-gray01 h-auto min-w-0 border-0 content-stretch p-[14px] relative rounded-[8px] shrink-0'
                aria-label={t('Go back', '뒤로 가기')}
                icon={
                  <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 size-[20px]'>
                    <NavigationLeftIcon20 className='w-full h-full' />
                  </div>
                }
              />
            </div>
          </div>
          <div className='flex flex-[1_0_0] flex-row items-center self-stretch'>
            <div className='content-stretch flex flex-[1_0_0] flex-col gap-[8px] h-full items-center justify-center min-h-px min-w-px relative'>
              <p
                className={`${applyTypography(
                  'title3',
                )} text-text-primary text-ellipsis overflow-hidden`}
              >
                {headerTitle}
              </p>
            </div>
          </div>
          <div className='w-[48px] shrink-0' />
        </div>
      </div>

      <div className='flex flex-col items-center pb-[120px] tabletH:pb-[160px] tabletH:pt-0 px-0 relative shrink-0 w-full max-w-[570px]'>
        <div className='flex flex-col items-center pb-[24px] pt-0 px-0 relative shrink-0 w-full'>
          <div className='relative flex items-center px-0 pt-0 shrink-0'>
            <div
              className={`${
                pageState === 'confirmed'
                  ? 'bg-green-light02'
                  : 'bg-grayscale-gray02'
              } overflow-clip relative rounded-full shrink-0 size-[90px] flex items-center justify-center`}
            >
              <div className='absolute rounded-full size-[60px] flex items-center justify-center'>
                <CheckCircleIcon
                  className={`w-full h-full ${
                    pageState === 'confirmed'
                      ? 'text-icon-success'
                      : 'text-icon-secondary'
                  }`}
                />
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-[8px] items-center relative shrink-0 w-full'>
            <p
              className={`hidden tabletH:block ${applyTypography(
                'h7',
              )} text-text-primary shrink-0 text-center w-full whitespace-pre-wrap`}
              style={{ lineHeight: '1.2' }}
            >
              {t('Booking Confirmed', '예약 완료')}
            </p>
          </div>
        </div>

        <div className='flex flex-col gap-[16px] tabletH:gap-[24px] items-start relative shrink-0 w-full'>
          <div className='bg-grayscale-gray00 border-[1.6px] border-grayscale-gray02 border-solid flex flex-col items-start overflow-clip p-[16px] tabletH:p-[40px] relative rounded-[8px] shrink-0 w-full'>
            <div className='border-grayscale-gray02 border-b-[1.6px] border-dashed pb-[16px] border-l-0 border-r-0 border-t-0 flex flex-col items-start tabletH:pb-[24px] pt-0 px-0 relative shrink-0 w-full'>
              <p
                className={`tabletH:hidden ${applyTypography(
                  'b14Strong',
                )} text-green-deep04 shrink-0`}
                style={{ lineHeight: '1.4' }}
              >
                {headline}
              </p>
              <p
                className={`hidden tabletH:block ${applyTypography(
                  'b18Strong',
                )} text-green-deep04 shrink-0`}
                style={{ lineHeight: '1.5' }}
              >
                {headline}
              </p>
              <p
                className={`tabletH:hidden ${applyTypography(
                  'title1',
                )} text-green-deep04 shrink-0 min-w-full w-[min-content] whitespace-pre-wrap`}
                style={{ lineHeight: '1.2' }}
              >
                {userName}!
              </p>
              <p
                className={`hidden tabletH:block ${applyTypography(
                  'h5',
                )} text-green-deep04 shrink-0 min-w-full w-[min-content] whitespace-pre-wrap`}
                style={{ lineHeight: '1.2' }}
              >
                {userName}!
              </p>
            </div>

            {bookingItems.map((bookingItem) => {
              const workshop = bookingItem.workshop;
              const dateSlot = bookingItem.dateSlot;

              const formattedDate = dateSlot?.date
                ? formatDate(dateSlot.date)
                : '';
              const formattedTime =
                dateSlot?.startTime && dateSlot?.endTime
                  ? `${formatTime(dateSlot.startTime)} - ${formatTime(
                      dateSlot.endTime,
                    )}`
                  : '';

              const workshopName =
                workshop?.title ||
                paymentData?.orderName ||
                t('Workshop name', '워크숍 이름');
              const workshopImage =
                workshop?.images && workshop.images.length > 0
                  ? workshop.images[0]
                  : '';
              const locationName = workshop?.meetingPoint?.name || '';
              const locationAddress = workshop?.meetingPoint?.address || '';

              return (
                <React.Fragment key={bookingItem.bookingId}>
                  <div className='border-grayscale-gray02 border-b-[1.6px] border-dashed border-l-0 border-r-0 border-t-0 flex flex-col gap-[6px] tabletH:gap-[8px] items-start px-0 pt-[8px] pb-[16px] tabletH:py-[24px] relative shrink-0 w-full'>
                    <p
                      className={`${applyTypography(
                        'b14Strong',
                      )} text-text-primary shrink-0`}
                      style={{ lineHeight: '1.4' }}
                    >
                      {t('Details', '상세 정보')}
                    </p>
                    <div className='flex gap-[16px] items-start relative shrink-0 w-full'>
                      <div className='relative rounded-[8px] shrink-0 size-[120px] overflow-hidden'>
                        <div className='absolute inset-0 pointer-events-none rounded-[8px]'>
                          <div className='absolute bg-grayscale-gray01 inset-0 rounded-[8px]' />
                          {workshopImage ? (
                            <img
                              alt={t('Workshop', '워크숍')}
                              className='absolute max-w-none object-cover object-center rounded-[8px] size-full'
                              src={workshopImage}
                            />
                          ) : (
                            <div className='absolute inset-0 bg-grayscale-gray01 rounded-[8px]' />
                          )}
                        </div>
                      </div>

                      <div className='flex flex-[1_0_0] flex-col gap-[12px] items-start justify-center min-h-px min-w-px relative shrink-0'>
                        <div className='relative flex flex-col items-start w-full not-italic whitespace-pre-wrap shrink-0'>
                          <p
                            className={`${applyTypography(
                              'b16Strong',
                            )} text-text-primary shrink-0 w-full`}
                            style={{ lineHeight: '1.5' }}
                          >
                            {workshopName}
                          </p>
                          {!CONFIRMED_STATUSES.includes(
                            String(
                              bookingItem.booking?.status || '',
                            ).toUpperCase(),
                          ) && (
                            <p
                              className={`${applyTypography(
                                'b12Strong',
                              )} text-text-accent shrink-0`}
                              style={{ lineHeight: '1.4' }}
                            >
                              {statusLabel(bookingItem.booking?.status)}
                            </p>
                          )}
                          <p
                            className={`${applyTypography(
                              'b13',
                            )} text-text-tertiary shrink-0 w-full`}
                            style={{ lineHeight: '1.4' }}
                          >
                            {locationName && locationAddress
                              ? `${locationName}, ${locationAddress}`
                              : locationAddress || locationName || ''}
                          </p>
                        </div>
                        <div className='relative flex flex-col items-start w-full not-italic shrink-0 text-text-primary'>
                          <p
                            className={`${applyTypography(
                              'b12Strong',
                            )} shrink-0`}
                            style={{ lineHeight: '1.4' }}
                          >
                            {t('Date', '날짜')}
                          </p>
                          <p
                            className={`${applyTypography(
                              'b14Strong',
                            )} min-w-full w-[min-content] whitespace-pre-wrap shrink-0`}
                            style={{ lineHeight: '1.4' }}
                          >
                            {formattedDate && formattedTime
                              ? `${formattedDate} - ${formattedTime}`
                              : formattedDate || formattedTime || '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='border-grayscale-gray02 border-b-[1.6px] border-dashed border-l-0 border-r-0 border-t-0 flex flex-col gap-[6px] tabletH:gap-[8px] items-start px-0 pt-[8px] pb-[16px] tabletH:py-[24px] relative shrink-0 w-full'>
                    <p
                      className={`${applyTypography(
                        'b14Strong',
                      )} text-text-primary shrink-0`}
                      style={{ lineHeight: '1.4' }}
                    >
                      {t('Booking number', '예약 번호')}
                    </p>
                    <div className='bg-grayscale-gray01 flex items-center justify-between p-[12px] tabletH:p-[16px] relative rounded-[8px] shrink-0 w-full'>
                      <p
                        className={`flex-[1_0_0] ${applyTypography(
                          'b16Strong',
                        )} text-text-primary min-h-px min-w-px shrink-0 whitespace-pre-wrap`}
                        style={{ lineHeight: '1.5' }}
                      >
                        {bookingItem.booking?.bookingSerialNumber || '-'}
                      </p>
                      <BaseButton
                        type='text'
                        size='s'
                        onClick={() =>
                          handleCopy(bookingItem.booking?.bookingSerialNumber)
                        }
                        isDisabled={!bookingItem.bookingId}
                        className='content-stretch min-w-0 rounded-none border-0 bg-transparent hover:bg-transparent overflow-clip p-[2px] relative shrink-0 size-[24px] hover:opacity-80 transition-opacity [&>span]:h-full'
                        aria-label={t('Copy booking number', '예약 번호 복사')}
                        icon={
                          <CopyIcon className='w-full h-full text-icon-primary' />
                        }
                      />
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

            {/* 계좌이체 결제는 PG 영수증이 없다 — receiptUrl 이 있을 때만 버튼을 그린다 */}
            {paymentData?.receiptUrl && (
            <div className='flex flex-col items-start pb-0 pt-[8px] tabletH:pt-[24px] px-0 relative shrink-0 w-full'>
              <BaseButton
                type='primary'
                size='l'
                typo={false}
                gap='gap-[2px] tabletH:gap-[8px]'
                iconPosition='after'
                icon={
                  <div className='overflow-clip relative shrink-0 size-[20px] flex items-center justify-center'>
                    <DownloadIcon className='w-full h-full text-icon-inverse' />
                  </div>
                }
                onClick={onDownloadReceipt}
                textWrapperClass='h-auto flex-none'
                className='bg-green-deep02 hover:bg-green-deep02 h-auto min-w-0 border-0 pl-[16px] pr-[12px] py-[12px] tabletH:py-[14px] relative rounded-[8px] shrink-0 w-full hover:opacity-90 transition-opacity'
              >
                <p
                  className={`${applyTypography(
                    'b16Strong',
                  )} text-text-inverse shrink-0`}
                  style={{ lineHeight: '1.5' }}
                >
                  {t('Download Receipt', '영수증 다운로드')}
                </p>
              </BaseButton>
            </div>
            )}
          </div>

          <BaseButton
            type='text'
            size='l'
            hideIcon
            typo={false}
            isStatic
            onClick={onThingsToKnow}
            textWrapperClass='h-auto flex-none'
            className='bg-grayscale-gray00 h-[48px] min-w-0 cursor-pointer border-0 px-[16px] py-[12px] relative rounded-[8px] shrink-0 w-full hover:opacity-80 transition-opacity'
          >
            <p
              className={`${applyTypography(
                'b16Strong',
              )} text-text-tertiary shrink-0 text-center`}
              style={{ lineHeight: '1.5' }}
            >
              {t('Things to know', '알아두세요')}
            </p>
          </BaseButton>

          <BaseButton
            type='text'
            size='l'
            hideIcon
            typo={false}
            isStatic
            onClick={onCancellationPolicy}
            textWrapperClass='h-auto flex-none'
            className='bg-grayscale-gray00 h-[48px] min-w-0 cursor-pointer border-0 px-[16px] py-[12px] relative rounded-[8px] shrink-0 w-full hover:opacity-80 transition-opacity'
          >
            <p
              className={`${applyTypography(
                'b16Strong',
              )} text-text-tertiary shrink-0 text-center`}
              style={{ lineHeight: '1.5' }}
            >
              {t('Cancellation & refund policy', '취소 및 환불 정책')}
            </p>
          </BaseButton>
        </div>
      </div>

      {recommendedWorkshops && recommendedWorkshops.length > 0 && (
        <div className='flex gap-[24px] items-center relative shrink-0 justify-center flex-wrap'>
          {recommendedWorkshops.map((workshop, index) => (
            <div key={index} className='w-[374px]'>
              <WorkshopCard
                title={workshop.title}
                description={workshop.description}
                keywords={workshop.keywords}
                tags={workshop.tags}
                image={workshop.image}
                isWishlisted={workshop.isWishlisted}
                onToggleWishlist={workshop.onToggleWishlist}
              />
            </div>
          ))}
        </div>
      )}
    </div>

      <RefundModal
        isOpen={infoModal === 'thingsToKnow'}
        onClose={() => setInfoModal(null)}
        title={t('Things to know', '알아두세요')}
        content={thingsToKnowText}
        emptyText={t(
          'No additional notes for this workshop.',
          '이 워크숍에 대한 추가 안내가 없습니다.',
        )}
      />

      {copyToast}

      <RefundModal
        isOpen={infoModal === 'refundPolicy'}
        onClose={() => setInfoModal(null)}
        content={refundPolicyText || ''}
      />
    </>
  );
};

export default BookingConfirmed;
